import { useLocation, useSearchParams } from 'react-router-dom';
import type { DataTableSort } from '../components/primitives';

export interface UseTableSortOptions {
  /** The column the table is ordered by before anyone clicks a header. */
  readonly defaultSort?: DataTableSort | undefined;
  /**
   * Distinguishes the sort of one table from another on the same page: the
   * URL carries `<prefix>sort` and `<prefix>dir`. Defaults to none, i.e.
   * `?sort=…&dir=…`.
   */
  readonly prefix?: string | undefined;
}

/**
 * Column sort for a portal table, kept in the URL so a sorted view can be
 * shared and survives a reload. Clicking the active column flips direction;
 * clicking another column sorts it ascending; the page resets.
 *
 * @param allowedKeys - Sort keys the table understands; anything else in the URL is ignored.
 * @param options - The default order and, for a second table on the page, a param prefix.
 * @returns The active sort and the header click handler for `DataTable`.
 */
export function useTableSort(
  allowedKeys: readonly string[],
  options: UseTableSortOptions = {},
): {
  readonly sort: DataTableSort | undefined;
  readonly onSort: (key: string) => void;
} {
  const [params, setParams] = useSearchParams();
  // Kept across the URL rewrite, so the page's Back still knows its origin.
  const { state } = useLocation();
  const prefix = options.prefix ?? '';
  const sortParam = `${prefix}sort`;
  const dirParam = `${prefix}dir`;

  const key = params.get(sortParam);
  const direction = params.get(dirParam) === 'desc' ? 'desc' : 'asc';
  const sort: DataTableSort | undefined =
    key !== null && allowedKeys.includes(key) ? { key, direction } : options.defaultSort;

  function onSort(nextKey: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const flipped = sort?.key === nextKey && sort.direction === 'asc' ? 'desc' : 'asc';
        next.set(sortParam, nextKey);
        next.set(dirParam, sort?.key === nextKey ? flipped : 'asc');
        next.delete(`${prefix}page`);
        return next;
      },
      { replace: true, state },
    );
  }

  return { sort, onSort };
}
